import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, 
  Button, Box, CircularProgress, Link, List, ListItem, ListItemText, Card, CardContent, Grid, Divider } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import axios from 'axios';
import { downloadResumeFile } from '../../../helper/organizationsApis';
import { Organizationagetlearners } from "../../../helper/base_url";

const UserDetailsDialog = ({ open, onClose, userId }) => {
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && userId) {
      const fetchUserDetails = async () => {
        setLoading(true);
        setError(null);
        try {
          const token = localStorage.getItem('company_token');
          const response = await axios.get(`${Organizationagetlearners}/${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUserDetails(response.data.user);
        } catch (err) {
          //console.error('Error fetching user details:', err);
          setError('Failed to load user details');
        } finally {
          setLoading(false);
        }
      };
      fetchUserDetails();
    }
    if (!open) {
      setUserDetails(null);
      setError(null);
    }
  }, [open, userId]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          <Typography color="error" textAlign="center">{error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!userDetails) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          <Typography>No user details available.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const {
    name,
    email,
    mobile,
    country,
    linkedinProfile,
    education,
    employmentHistory,
    resume,
    languages,
    keySkills,
    projects,
    additionalInformation,
    currentOccupation,
    dateofbirth,
    experience,
    resumeHeadline,
  } = userDetails;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
    <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
        position: 'absolute',
        right : 8,
        top : 8,
        color: (theme) => theme.palette.grey[500],
        }}
      >
      <CloseIcon />
      </IconButton>
      <DialogTitle sx={{ fontWeight: 700, color: '#052659' }}>
        User Details - {name || 'Unnamed'}
      </DialogTitle>
      <DialogContent dividers>

        {/* Basic Info */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <EmailIcon color="primary" sx={{ mr: 1 }} />
                  <Typography>Email: {email || 'N/A'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <PhoneIcon color="primary" sx={{ mr: 1 }} />
                  <Typography>Mobile: {mobile || 'N/A'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                  <Typography>Country: {country || 'N/A'}</Typography>
                </Box>
              </Grid>
              {linkedinProfile && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    <LinkedInIcon color="primary" sx={{ mr: 1 }} />
                    <Link href={linkedinProfile} target="_blank" rel="noopener noreferrer">
                      LinkedIn Profile
                    </Link>
                  </Box>
                </Grid>
              )}
              {dateofbirth && (
                <Grid item xs={12} sm={6}>
                  <Typography>Date of Birth: {dateofbirth}</Typography>
                </Grid>
              )}
              {currentOccupation && (
                <Grid item xs={12} sm={6}>
                  <Typography>Current Occupation: {currentOccupation}</Typography>
                </Grid>
              )}
              {experience && (
                <Grid item xs={12} sm={6}>
                  <Typography>Experience: {experience}</Typography>
                </Grid>
              )}
              {resumeHeadline && (
                <Grid item xs={12}>
                  <Typography>Resume Headline: {resumeHeadline}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Education */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
            <Typography variant="h6" sx={{ color: '#043885', fontWeight: 600 }}>
             Education  <SchoolIcon/>
            </Typography>
            {education?.length ? (
            <List>
                {education.map((edu) => (
                <ListItem key={edu._id} alignItems="flex-start">
                    <ListItemText
                    primary={
                        <>
                        <Typography variant="subtitle1" fontWeight={500}>
                            Highest Education: {edu.highesteducation || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Institute Name: {edu.instituteName || 'N/A'}
                        </Typography>
                        </>
                    }
                    secondary={
                        <>
                        <Typography variant="body2">Graduation Year: {edu.yearOfGraduation ?? 'N/A'}</Typography>
                        <Typography variant="body2">Courses/Projects: {edu.relevantCoursesProjects || 'N/A'}</Typography>
                        </>
                    }
                    />
                </ListItem>
                ))}
            </List>
            ) : (
            <Typography>No education details provided.</Typography>
            )}
        </CardContent>
        </Card>

        {/* Employment */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
              Employment History
            </Typography>
            {employmentHistory?.length > 0 ? (
              <List dense>
                {employmentHistory.map((job, idx) => (
                  <ListItem key={job._id || idx}>
                    <ListItemText
                      primary={`${job.position || 'Position'} @ ${job.companyName || 'Company'}`}
                      secondary={
                        <>
                          <Typography variant="body2">Duration: {job.startDate || 'N/A'} - {job.endDate || 'Present'}</Typography>
                          <Typography variant="body2">{job.description || 'No description'}</Typography>
                        </>
                      }
                    />
                    <WorkIcon sx={{ ml: 2 }} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography>No employment history provided.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Resume */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
              Resume
            </Typography>
            {resume?.length > 0 ? (
              resume.map((res, i) => (
                <Box key={i} display="flex" alignItems="center" mb={1}>
                  <InsertDriveFileIcon sx={{ mr: 1 }} />
                  <Typography sx={{ mr: 2 }}>{res.originalname || 'Resume File'}</Typography>

                  {res.path ? (
                    <>
                      {/* <Link href={res.path} target="_blank" rel="noopener noreferrer">
                        View Resume
                      </Link> */}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => downloadResumeFile(email, res.path)}
                        sx={{ ml: 2 }}
                      >
                        Download
                      </Button>
                    </>
                  ) : (
                    <Typography>No resume URL</Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography>No resume uploaded.</Typography>
            )}
          </CardContent>
        </Card>

        {/* Skills & Languages */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
          <CardContent>
            {keySkills?.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
                  Key Skills
                </Typography>
                <Typography>{keySkills.join(', ')}</Typography>
                <Divider sx={{ my: 2 }} />
              </>
            )}
            {languages?.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
                  Languages
                </Typography>
                <Typography>{languages.join(', ')}</Typography>
              </>
            )}
          </CardContent>
        </Card>
        {/* Projects */}
        <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
            Projects
            </Typography>
            {projects?.length > 0 ? (
            <List dense>
                {projects.map((project) => (
                <ListItem key={project._id} alignItems="flex-start">
                    <ListItemText
                    primary={
                        <Typography variant="subtitle1" fontWeight={500}>
                        {project.projectTitle || 'Untitled Project'}
                        </Typography>
                    }
                    secondary={
                        <>
                        <Typography variant="body2">
                            Description: {project.description || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                            Technologies: {project.technologies || 'N/A'}
                        </Typography>
                        </>
                    }
                    />
                </ListItem>
                ))}
            </List>
            ) : (
            <Typography>No projects provided.</Typography>
            )}
        </CardContent>
        </Card>
        {/* Additional Info */}
        {additionalInformation && (
          <Card sx={{ mb: 1, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, color: '#043885', fontWeight: 600 }}>
                Additional Information
              </Typography>
              <Typography>{additionalInformation}</Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailsDialog;
